/* SANA Jewels — Lightweight Wishlist (no app, no fees)
   Stores saved product handles in the shopper's browser via localStorage.
   Per-browser only (not synced across devices or to a customer account) —
   that trade-off is what keeps this free and app-free. Powers:
   - Heart toggle buttons anywhere on the site (data-sana-wishlist-toggle)
   - The header wishlist count badge (data-sana-wishlist-count)
   - The /pages/favorites listing page
   - The share button (data-sana-share-toggle) — native share sheet or
     copy-to-clipboard fallback, driven by data attributes rather than an
     inline onclick so product titles with quotes/apostrophes can't break
     the HTML attribute they'd otherwise be embedded in.

   V24 (Sep 2026): now loaded ONCE sitewide from layout/theme.liquid (defer)
   because product cards carry a heart toggle on every grid. The PDP snippet
   and the favorites template still include their own <script> tag; the
   guard below makes a second execution a no-op, so click handlers are never
   registered twice (which would toggle-then-untoggle on every click).
*/
(function () {
  "use strict";

  if (window.SanaWishlist) return;

  var STORAGE_KEY = "sana_wishlist";

  function getWishlist() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* localStorage unavailable (private browsing, quota, etc.) — fail silently */
    }
    document.dispatchEvent(new CustomEvent("sana:wishlist:updated", { detail: { list: list } }));
  }

  function isSaved(handle) {
    return getWishlist().indexOf(handle) > -1;
  }

  function toggle(handle) {
    var list = getWishlist();
    var idx = list.indexOf(handle);
    if (idx > -1) {
      list.splice(idx, 1);
    } else {
      list.push(handle);
    }
    saveWishlist(list);
    return list.indexOf(handle) > -1;
  }

  function refreshUI() {
    var count = getWishlist().length;

    document.querySelectorAll("[data-sana-wishlist-toggle]").forEach(function (btn) {
      var handle = btn.getAttribute("data-product-handle");
      var saved = isSaved(handle);
      btn.classList.toggle("is-saved", saved);
      btn.setAttribute("aria-pressed", saved ? "true" : "false");
      var label = btn.querySelector(".visually-hidden");
      if (label) {
        label.textContent = saved ? "Remove from favorites" : "Save to favorites";
      }
    });

    document.querySelectorAll("[data-sana-wishlist-count]").forEach(function (el) {
      el.textContent = count > 0 ? count : "";
      el.hidden = count === 0;
    });
  }

  function handleShareClick(btn) {
    var title = btn.getAttribute("data-share-title") || document.title;
    var url = btn.getAttribute("data-share-url") || window.location.href;

    if (navigator.share) {
      navigator.share({ title: title, url: url }).catch(function () {
        /* user cancelled the native share sheet — no action needed */
      });
      return;
    }

    var fallbackCopy = function () {
      btn.setAttribute("data-copied", "true");
      setTimeout(function () {
        btn.removeAttribute("data-copied");
      }, 1800);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(fallbackCopy).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  }

  document.addEventListener("click", function (e) {
    var wishlistBtn = e.target.closest("[data-sana-wishlist-toggle]");
    if (wishlistBtn) {
      e.preventDefault();
      var handle = wishlistBtn.getAttribute("data-product-handle");
      if (handle) {
        toggle(handle);
        refreshUI();
      }
      return;
    }

    var shareBtn = e.target.closest("[data-sana-share-toggle]");
    if (shareBtn) {
      e.preventDefault();
      handleShareClick(shareBtn);
    }
  });

  document.addEventListener("sana:wishlist:updated", refreshUI);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refreshUI);
  } else {
    refreshUI();
  }

  window.SanaWishlist = {
    get: getWishlist,
    isSaved: isSaved,
    toggle: toggle,
    refresh: refreshUI
  };
})();
